import { createContext, FC, ReactElement, useCallback, useContext, useState } from 'react'
import config from '../../config'
import { applyRuleAsync, ApplyRulesExam, createExtraction, ExtractedResponse, getAsyncResponse } from '../../externals/api/applyRules'
import { getSignedUploadUrl, uploadToS3 } from '../../externals/api/upload'
import { generateSha1OfFile } from '../../utils/generateSha1OfFile'
import { sleep } from '../../utils/sleep'
import { useAlertHandler } from '../alertHandler'

export type ProcessPdf = {
  labCode: string;
  age: string;
  imc: string;
  gender: string;
  files: File[];
  labId?: string;
  patientId?: string;
  pregnant?: null | 'not-pregnant' | 'pregnant';
  hasMenopause?: 'menopause' | 'not-menopause';
  processType?: 'empty' | 'user-selected';
}

export type ApplyRules = {
  age: string;
  imc: string;
  gender: string;
  exams: ApplyRulesExam[];
  patientId?: string;
  pregnant?: null | 'not-pregnant' | 'pregnant';
  hasMenopause?: 'menopause' | 'not-menopause';
  processType?: 'empty' | 'user-selected';
}

interface IProcessExam {
  parameters: Partial<ProcessPdf>;
  result: ExtractedResponse;
  processPdf: (data: ProcessPdf) => Promise<string>;
  applyRules: (data: ApplyRules) => Promise<string>;
  getProcessedExam: (key: string) => Promise<ExtractedResponse>;
  setParameters: (params: Partial<ProcessPdf>) => void;
}

const ProcessExamContext = createContext<IProcessExam>({} as IProcessExam)

export const useProcessExam = (): IProcessExam => {
  const context = useContext(ProcessExamContext)
  if (!context) {
    throw new Error('useProcessExam must be used within an ProcessExamContext')
  }

  return context
}

export const ProcessExamProvider: FC = ({ children }): ReactElement => {
  const [result, setResult] = useState({} as ExtractedResponse)
  const [parameters, _setParameters] = useState({} as Partial<ProcessPdf>)
  const [activeKey, setActiveKey] = useState('')
  const { showError } = useAlertHandler()

  const setParameters = useCallback((params: Partial<ProcessPdf>): void => {
    _setParameters(oldParameters => ({ ...oldParameters, ...params }))
  }, [_setParameters])

  const getProcessedExam = useCallback(async (key: string): Promise<ExtractedResponse> => {
    if (activeKey === key && !result.error) return result

    for (let i = 0; i < 60; i++) {
      await sleep(5100)
      const { status, ...restResponse } = await getAsyncResponse(key)
      if (status !== 'pending') {
        setActiveKey(key)
        setResult(restResponse)
        return restResponse
      }
    }

    setActiveKey(key)
    setResult({ status: 'error', error: true } as ExtractedResponse)

    throw new Error('INVALID_PROCESSING_FAILED_TIMEOUT')
  }, [activeKey, result, setActiveKey, setResult])

  const processPdf = useCallback(async (data: ProcessPdf): Promise<string> => {
    try {
      const sha1OfFiles = await Promise.all(data.files.map(file => generateSha1OfFile(file)))

      const createdExtraction = await createExtraction({
        ...data,
        pregnant: data.gender === 'male' ? undefined : data.pregnant,
        hasMenopause: data.gender === 'male' ? undefined : data.hasMenopause,
        files: sha1OfFiles.map(sha1 => ({
          sha1,
          labCode: data.labCode
        }))
      })

      await Promise.all(createdExtraction.files.map(async (uploadFile, i): Promise<void> => {
        if (uploadFile.isUploaded === false) {
          const { headers, url } = await getSignedUploadUrl({
            genDiffFilename: false,
            filename: `${uploadFile.sha1}.pdf`,
            contentType: data.files[i].type,
            folder: createdExtraction.path,
            environment: config.ENVIRONMENT
          })

          const newHeaders = {
            ...headers,
            'Content-Type': data.files[i].type
          }

          await uploadToS3(url, data.files[i], newHeaders)
        }
      }))

      for (let i = 0; i < 60; i++) {
        await sleep(5100)
        const { status, ...restResponse } = await getAsyncResponse(createdExtraction.key)
        if (status !== 'pending') {
          setActiveKey(createdExtraction.key)
          setResult(restResponse)
          return createdExtraction.key
        }
      }

      setActiveKey(createdExtraction.key)
      setResult({ status: 'error', error: true } as ExtractedResponse)

      throw new Error('INVALID_PROCESSING_FAILED_TIMEOUT')
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setResult, setActiveKey, showError])

  const applyRules = useCallback(async (data: ApplyRules): Promise<string> => {
    try {
      const createdExtraction = await applyRuleAsync({
        ...data,
        pregnant: data.gender === 'male' ? undefined : data.pregnant,
        hasMenopause: data.gender === 'male' ? undefined : data.hasMenopause
      })

      for (let i = 0; i < 60; i++) {
        await sleep(5100)
        const { status, ...restResponse } = await getAsyncResponse(createdExtraction.key)
        if (status !== 'pending') {
          setActiveKey(createdExtraction.key)
          setResult(restResponse)
          return createdExtraction.key
        }
      }

      setActiveKey(createdExtraction.key)
      setResult({ status: 'error', error: true } as ExtractedResponse)

      throw new Error('INVALID_PROCESSING_FAILED_TIMEOUT')
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setResult, setActiveKey, showError])

  return (
    <ProcessExamContext.Provider
      value={{
        parameters,
        result,
        processPdf,
        getProcessedExam,
        setParameters,
        applyRules
      }}
    >
      {children}
    </ProcessExamContext.Provider>
  )
}
